import { faCheck, faMinus } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Checkbox as CheckboxPrimitive } from "radix-ui"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

export type CheckboxProps = ComponentProps<typeof CheckboxPrimitive.Root> & {
  /** Figma nennt das `title` — hier `label`, da `title` mit dem nativen HTML-Tooltip-Attribut kollidiert. */
  label?: ReactNode
  description?: ReactNode
  showLabel?: boolean
  showDescription?: boolean
  wrapperClassName?: string
}

function Checkbox({
  className,
  wrapperClassName,
  checked,
  disabled,
  label,
  description,
  showLabel = true,
  showDescription = false,
  ...props
}: CheckboxProps) {
  const control = (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      checked={checked}
      disabled={disabled}
      className={cn(
        "peer relative inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-md border-2 border-neutrals-v2-400 bg-backgrounds-primary transition-colors outline-none",
        "hover:border-neutrals-v2-600",
        "focus-visible:border-neutrals-v2-1000",
        "aria-invalid:border-error-red-200",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-neutrals-v2-400",
        "data-[state=checked]:border-0 data-[state=checked]:bg-brand-red-200 data-[state=checked]:text-neutrals-v2-100",
        "data-[state=indeterminate]:border-0 data-[state=indeterminate]:bg-brand-red-200 data-[state=indeterminate]:text-neutrals-v2-100",
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current"
      >
        <FontAwesomeIcon
          icon={checked === "indeterminate" ? faMinus : faCheck}
          className="size-3.5"
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )

  const showContent = (showLabel && label) || (showDescription && description)

  return (
    <label
      data-slot="checkbox-field"
      data-disabled={disabled || undefined}
      className={cn(
        "inline-flex items-start gap-2 p-0.5",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        wrapperClassName
      )}
    >
      {control}
      {showContent && (
        <span
          data-slot="checkbox-content"
          className="flex min-w-0 flex-1 flex-col justify-center gap-2 text-sm leading-5 font-normal"
        >
          {showLabel && label && (
            <span
              data-slot="checkbox-label"
              className={cn(
                "text-labels-primary",
                disabled && "text-labels-secondary"
              )}
            >
              {label}
            </span>
          )}
          {showDescription && description && (
            <span
              data-slot="checkbox-description"
              className="text-labels-secondary"
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  )
}

export { Checkbox }
